import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

/**
 * URL Param Controller
 * 
 * Updates URL query parameters based on input value on blur or Enter key press.
 * 
 * Usage:
 *   <input type='text' 
 *     name='search'
 *     data-controller="url-param"
 *     data-url-param-url-value="/search"
 *     data-action="blur->url-param#update keydown->url-param#onKeydown"
 *   />
 * 
 * Values:
 *   - url: The base URL to navigate to with the input value as a query parameter.
 *     Left out, the current location is the base — for a page whose other
 *     filters land on the URL through frame visits after this input rendered,
 *     so a fixed base would drop them.
 *   - turboFrame: optional Turbo frame id; when present, the update targets that
 *     frame (async filtering) instead of triggering a full page navigation
 *
 * `debouncedUpdate` is `update` for an `input` event: it waits for a pause in
 * typing so a search field filters as you type without a request per key.
 *
 * `updateOnSelect` is `update` for a typeahead's `app-select:change`, bound on its
 * hidden value input: it navigates on a pick or an explicit clear, but not on the
 * change the typeahead fires when typing over a pick empties the value — that
 * would reload the page under the user mid-word.
 */
export default class extends Controller {
  static values = { url: String, turboFrame: String }

  static DEBOUNCE_MS = 300

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
  }

  debouncedUpdate() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.timer = null
      this.update()
    }, this.constructor.DEBOUNCE_MS)
  }

  update() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }

    const url = new URL(this.urlValue || window.location.href, window.location.origin)
    const paramName = this.element.name
    const isCheckbox = this.element.type === "checkbox"
    const value = isCheckbox && !this.element.checked ? "" : this.element.value.trim()

    if (value) {
      url.searchParams.set(paramName, value)
    } else {
      url.searchParams.delete(paramName)
    }

    this.visit(url)
  }

  updateOnSelect(event) {
    if (event.detail?.reason === "input") return

    this.update()
  }

  visit(url) {
    if (this.hasTurboFrameValue && this.turboFrameValue) {
      Turbo.visit(url.toString(), { frame: this.turboFrameValue, action: "advance" })
    } else {
      window.location.href = url.toString()
    }
  }

  onKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.update()
    }
  }
}
